import { supabase } from '../lib/supabase';
import {
  UserProfile,
  VideoLog,
  CreditRequest,
  UserRole,
  Review,
  ReviewStatus,
  ReviewToken,
  ReviewStats,
  ReviewSubmissionData,
  ReviewProductTag,
  ReviewImport,
  WidgetSettings,
  InventorySettings
} from '../types';

export { reviewsApi } from './api/reviews';

// Supabase Edge Function URL prefix
const FUNCTION_BASE_URL = import.meta.env.VITE_SUPABASE_URL + '/functions/v1';

export const api = {
  // --- Auth & Profiles ---

  async getProfile(userId: string): Promise<UserProfile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
    return data as UserProfile;
  },

  // Update Admin Billing Details
  async updateAdminBillingDetails(userId: string, details: {
    admin_organization?: string;
    admin_website?: string;
    admin_gst?: string;
    admin_address?: string;
    admin_city?: string;
    admin_state?: string;
    admin_pincode?: string;
    admin_country?: string;
  }) {
    const { error } = await supabase
      .from('profiles')
      .update(details)
      .eq('id', userId);

    if (error) throw new Error(error.message);
  },

  async createPacker(adminId: string, packerData: { name: string; mobile: string; pin: string }) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/admin-create-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ ...packerData, role: 'packer', admin_id: adminId })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to create packer');
    }

    const text = await response.text();
    return text ? JSON.parse(text) : { success: true };
  },

  async deletePacker(packerId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/admin-user-actions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ action: 'delete', user_id: packerId })
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error('Failed to delete packer: ' + err);
    }
    return true;
  },

  async updatePackerPin(packerId: string, newPin: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/admin-user-actions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ action: 'update_pin', user_id: packerId, new_pin: newPin })
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error('Failed to update PIN: ' + err);
    }
    return true;
  },

  // --- Integrations & Google ---

  async updateIntegrationConfig(userId: string, config: any) {
    const { error } = await supabase
      .from('profiles')
      .update({ integrations: config })
      .eq('id', userId);
    if (error) throw error;
  },

  async getInventoryLogs(adminId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('inventory_logs')
      .select('*')
      .eq('admin_id', adminId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error("Error fetching logs", error);
      return [];
    }
    return data;
  },

  // 1. Get Google Auth URL
  async initiateGoogleAuth(adminId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/google-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ action: 'generate_auth_url', admin_id: adminId })
    });

    if (!response.ok) throw new Error("Failed to start Google Auth");

    const data = await response.json();

    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error("No URL returned from backend");
    }
  },

  async getGoogleAuthUrl(adminId: string) {
    return this.initiateGoogleAuth(adminId);
  },

  async exchangeGoogleCode(adminId: string, code: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/google-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        action: 'exchange_code',
        admin_id: adminId,
        code: code
      })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || "Failed to exchange code");
    }

    try {
      const { folderId } = await this.setupPowerpackInfrastructure(adminId);
      await supabase.from('profiles').update({
        integrations: {
          googleConnected: true,
          googleFolderId: folderId,
          googleSheetId: null
        }
      }).eq('id', adminId);
    } catch (error) {
      console.error("Folder setup failed:", error);
    }

    return await response.json();
  },

  async disconnectGoogle(adminId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    await fetch(`${FUNCTION_BASE_URL}/google-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ action: 'disconnect', admin_id: adminId })
    });

    await supabase.from('profiles').update({
      integrations: { googleConnected: false, googleFolderId: null, googleSheetId: null }
    }).eq('id', adminId);
  },

  async setupPowerpackInfrastructure(adminId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const listRes = await fetch(`${FUNCTION_BASE_URL}/google-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ action: 'list_folders', admin_id: adminId })
    });

    if (!listRes.ok) {
      const err = await listRes.json();
      throw new Error(err.error || "Failed to list folders");
    }

    const listData = await listRes.json();
    const folders = listData.folders || [];

    let powerpackFolder = folders.find((f: any) => f.name === 'Powerpack');

    if (!powerpackFolder) {
      const createRes = await fetch(`${FUNCTION_BASE_URL}/google-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ action: 'create_folder', name: 'Powerpack', admin_id: adminId })
      });

      if (!createRes.ok) {
        const err = await createRes.json();
        throw new Error(err.error || "Failed to create Powerpack folder");
      }
      powerpackFolder = await createRes.json();
    }

    return { folderId: powerpackFolder.id, sheetId: null };
  },

  // --- Logs & Videos ---

  async getLogs(userId: string, role: UserRole): Promise<VideoLog[]> {
    let query = supabase.from('logs').select('*, profiles:packer_id(name)').order('created_at', { ascending: false });

    if (role === UserRole.ADMIN) {
      query = query.eq('admin_id', userId);
    } else if (role === UserRole.PACKER) {
      query = query.eq('packer_id', userId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data.map((log: any) => ({
      ...log,
      packer_name: log.profiles?.name || 'Unknown'
    }));
  },

  async getHistoryLogs(userId: string, role: UserRole): Promise<any[]> {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    let query = supabase
      .from('logs')
      .select('*, profiles:packer_id(name)')
      .gte('created_at', thirtyDaysAgo.toISOString())
      .order('created_at', { ascending: false });

    if (role === UserRole.ADMIN) {
      query = query.eq('admin_id', userId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data.map((log: any) => ({
      ...log,
      packer_name: log.profiles?.name || 'Unknown'
    }));
  },

  // --- Upload Flow ---

  async getUploadToken(filename: string, contentType: string) {
    const session = (await supabase.auth.getSession()).data.session;
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/delegate-upload-token?filename=${filename}&contentType=${contentType}`, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Token Error: ${errText}`);
    }

    return response.json() as Promise<{ uploadUrl: string; folderId?: string; folderName?: string; fileId?: string; accessToken?: string; metadata?: string }>;
  },

  async uploadReviewMedia(adminId: string, file: File) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const formData = new FormData();
    formData.append('file', file);
    formData.append('admin_id', adminId);

    const response = await fetch(`${FUNCTION_BASE_URL}/upload-review-media`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      },
      body: formData
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || "Upload failed");
    }

    return response.json();
  },

  // --- FULFILLMENT ---

  async completeFulfillment(data: { awb: string; videoUrl: string; folder_id: string | null; duration?: number }) {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error("User is not authenticated. Cannot complete fulfillment.");
    }

    const response = await fetch(`${FUNCTION_BASE_URL}/fulfillment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        stage: 1,
        awb: data.awb,
        video_url: data.videoUrl,
        folder_id: data.folder_id,
        duration: data.duration
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Backend Error:", errorText);
      throw new Error(`Fulfillment failed: ${errorText}`);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : { success: true };
  },

  // --- Credits ---

  async getCreditRequests(role: UserRole, userId?: string): Promise<CreditRequest[]> {
    let query = supabase.from('credit_requests').select('*, profiles:admin_id(name)').order('created_at', { ascending: false });

    if (role === UserRole.ADMIN && userId) {
      query = query.eq('admin_id', userId);
    } else if (role === UserRole.SUPER_ADMIN) {
      query = query.eq('status', 'pending');
    }

    const { data, error } = await query;
    if (error) {
      console.warn("Credit requests fetch failed", error);
      return [];
    }

    return data.map((req: any) => ({
      ...req,
      admin_name: req.profiles?.name
    }));
  },

  async requestCredits(adminId: string, amount: number) {
    const { error } = await supabase.from('credit_requests').insert({
      admin_id: adminId,
      amount: amount,
      status: 'pending'
    });
    if (error) throw error;
  },

  async processCreditRequest(requestId: string, status: 'approved' | 'rejected') {
    const { error } = await supabase.from('credit_requests').update({ status }).eq('id', requestId);
    if (error) throw error;
  },

  async getPackers(adminId: string) {
    const { data, error } = await supabase.from('profiles')
      .select('*')
      .eq('role', 'packer')
      .eq('organization_id', adminId);

    if (error) throw error;
    return data;
  },

  async getAllAdmins() {
    const { data, error } = await supabase.from('profiles').select('*').eq('role', 'admin');
    if (error) throw error;
    return data;
  },

  // --- Inventory Management ---

  async getInventorySettings(adminId: string): Promise<InventorySettings | null> {
    const { data, error } = await supabase
      .from('inventory_settings')
      .select('*')
      .eq('admin_id', adminId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching inventory settings:', error);
      return null;
    }

    return {
      platform: data.platform,
      isEnabled: data.is_enabled,
      autoDraftProduct: data.auto_draft_product,
      autoActiveProduct: data.auto_active_product,
      autoDeleteVariant: data.auto_delete_variant,
      hideVariantsCss: data.hide_variants_css,
      lowStockThreshold: data.low_stock_threshold,
      syncFrequency: data.sync_frequency
    } as InventorySettings;
  },

  async updateInventorySettings(adminId: string, settings: Partial<InventorySettings>) {
    const dbPayload: any = {
      admin_id: adminId,
      updated_at: new Date().toISOString()
    };

    if (settings.platform !== undefined) dbPayload.platform = settings.platform;
    if (settings.isEnabled !== undefined) dbPayload.is_enabled = settings.isEnabled;
    if (settings.autoDraftProduct !== undefined) dbPayload.auto_draft_product = settings.autoDraftProduct;
    if (settings.autoActiveProduct !== undefined) dbPayload.auto_active_product = settings.autoActiveProduct;
    if (settings.lowStockThreshold !== undefined) dbPayload.low_stock_threshold = settings.lowStockThreshold;
    if (settings.autoDeleteVariant !== undefined) dbPayload.auto_delete_variant = settings.autoDeleteVariant;
    if (settings.hideVariantsCss !== undefined) dbPayload.hide_variants_css = settings.hideVariantsCss;

    const { data, error } = await supabase
      .from('inventory_settings')
      .upsert(dbPayload, { onConflict: 'admin_id' })
      .select()
      .single();

    if (error) {
      console.error('Error saving inventory settings:', error);
      throw error;
    }

    return {
      platform: data.platform,
      isEnabled: data.is_enabled,
      autoDraftProduct: data.auto_draft_product,
      autoActiveProduct: data.auto_active_product,
      lowStockThreshold: data.low_stock_threshold
    } as InventorySettings;
  },

  async toggleHideVariantsCSS(adminId: string, isEnabled: boolean) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/theme-actions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        action: isEnabled ? 'enable' : 'disable',
        admin_id: adminId
      })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || "Failed to update theme");
    }
    return response.json();
  },

  // ==========================================
  // REVIEW SYSTEM API METHODS
  // ==========================================

  async getReviewsPaginated(
    userId: string,
    page: number = 1,
    pageSize: number = 50,
    filters: {
      status: string;
      rating?: number | null;
      hasMedia?: boolean;
      search?: string
    }
  ) {
    let query = supabase
      .from('reviews')
      .select('*', { count: 'exact' })
      .eq('admin_id', userId);

    if (filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters.rating) {
      query = query.eq('rating', filters.rating);
    }

    if (filters.hasMedia) {
      query = query.not('media_urls', 'is', null).neq('media_urls', '[]');
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, count, error } = await query
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw error;
    return { data: data || [], total: count || 0 };
  },

  async getReviewCounts(userId: string) {
    const [all, pending, approved, rejected] = await Promise.all([
      supabase.from('reviews').select('*', { count: 'exact', head: true }).eq('admin_id', userId),
      supabase.from('reviews').select('*', { count: 'exact', head: true }).eq('admin_id', userId).eq('status', 'pending'),
      supabase.from('reviews').select('*', { count: 'exact', head: true }).eq('admin_id', userId).eq('status', 'approved'),
      supabase.from('reviews').select('*', { count: 'exact', head: true }).eq('admin_id', userId).eq('status', 'rejected'),
    ]);

    return {
      all: all.count || 0,
      pending: pending.count || 0,
      approved: approved.count || 0,
      rejected: rejected.count || 0
    };
  },

  async getReviewsForAdmin(adminId: string, filters?: {
    status?: 'pending' | 'approved' | 'rejected';
    product_id?: string;
    min_rating?: number;
    limit?: number;
  }): Promise<Review[]> {
    let query = supabase
      .from('reviews')
      .select('*')
      .eq('admin_id', adminId)
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.product_id) {
      query = query.eq('product_id', filters.product_id);
    }

    if (filters?.min_rating) {
      query = query.gte('rating', filters.min_rating);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Review[];
  },

  async updateReviewStatus(reviewId: string, status: ReviewStatus): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', reviewId);

    if (error) throw error;
  },

  async addAdminResponse(reviewId: string, responseText: string): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .update({
        response_text: responseText,
        response_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', reviewId);

    if (error) throw error;
  },

  async deleteReview(reviewId: string): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', reviewId);

    if (error) throw error;
  },

  async updateReview(reviewId: string, updates: Partial<Review>): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', reviewId);

    if (error) throw error;
  },

  async getReviewStats(adminId: string, productId?: string): Promise<ReviewStats> {
    let query = supabase
      .from('reviews')
      .select('rating, status, verified_purchase')
      .eq('admin_id', adminId)
      .eq('status', 'approved');

    if (productId) {
      query = query.eq('product_id', productId);
    }

    const { data, error } = await query;
    if (error) throw error;

    const reviews = data || [];
    const total = reviews.length;
    const average = total > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / total
      : 0;

    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => {
      // @ts-ignore
      if (distribution[r.rating] !== undefined) distribution[r.rating]++;
    });

    const { count: pendingCount } = await supabase
      .from('reviews')
      .select('*', { count: 'exact', head: true })
      .eq('admin_id', adminId)
      .eq('status', 'pending');

    const verifiedCount = reviews.filter(r => r.verified_purchase).length;

    return {
      total,
      average: parseFloat(average.toFixed(1)),
      rating_distribution: distribution,
      pending_count: pendingCount || 0,
      verified_count: verifiedCount
    };
  },

  async submitPublicReview(data: ReviewSubmissionData): Promise<{ success: boolean; review_id?: string }> {
    const response = await fetch(`${FUNCTION_BASE_URL}/public-review-submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Review submission failed');
    }

    return response.json();
  },

  async validateReviewToken(token: string): Promise<ReviewToken | null> {
    const { data, error } = await supabase
      .from('review_tokens')
      .select('*')
      .eq('token', token)
      .is('used_at', null)
      .single();

    if (error) return null;

    const expiresAt = new Date(data.expires_at);
    if (expiresAt < new Date()) return null;

    return data as ReviewToken;
  },

  async getWidgetSettings(adminId: string): Promise<WidgetSettings | null> {
    const { data, error } = await supabase
      .from('widget_settings')
      .select('*')
      .eq('admin_id', adminId)
      .single();

    if (error) return null;
    return data as WidgetSettings;
  },

  async updateWidgetSettings(adminId: string, settings: Partial<WidgetSettings>): Promise<void> {
    const { error } = await supabase
      .from('widget_settings')
      .upsert({
        admin_id: adminId,
        ...settings,
        updated_at: new Date().toISOString()
      }, { onConflict: 'admin_id' });

    if (error) throw error;
  },

  async getProductTags(adminId: string): Promise<ReviewProductTag[]> {
    const { data, error } = await supabase
      .from('review_product_tags')
      .select('*')
      .eq('admin_id', adminId);

    if (error) throw error;
    return data as ReviewProductTag[];
  },

  async createProductTag(adminId: string, tagName: string, productIds: string[]): Promise<void> {
    const { error } = await supabase
      .from('review_product_tags')
      .insert({
        admin_id: adminId,
        tag_name: tagName,
        product_ids: productIds
      });

    if (error) throw error;
  },

  async importReviewsFromCSV(adminId: string, csvData: any[], source: 'judgeme' | 'loox' | 'custom'): Promise<ReviewImport> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/import-reviews`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        admin_id: adminId,
        source,
        reviews: csvData
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Import failed');
    }

    return response.json();
  },

  subscribeToReviews(adminId: string, callback: (payload: any) => void) {
    return supabase
      .channel(`reviews:${adminId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reviews',
          filter: `admin_id=eq.${adminId}`
        },
        callback
      )
      .subscribe();
  },

  async getReviewsForProduct(
    adminId: string,
    productId: string,
    options?: {
      groupByTags?: boolean;
      groupingTagKey?: 'first' | 'product_type' | 'category';
      maxReviews?: number;
      status?: 'approved' | 'pending' | 'rejected';
    }
  ): Promise<Review[]> {
    const {
      groupByTags = false,
      groupingTagKey = 'first',
      maxReviews = 300,
      status = 'approved'
    } = options || {};

    if (!groupByTags) {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('admin_id', adminId)
        .eq('product_id', productId)
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Review[];
    }

    const { data: sampleReview, error: sampleError } = await supabase
      .from('reviews')
      .select('product_tags, product_type')
      .eq('product_id', productId)
      .limit(1)
      .single();

    if (sampleError || !sampleReview?.product_tags?.length) {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('admin_id', adminId)
        .eq('product_id', productId)
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Review[];
    }

    let groupingTag: string;

    switch (groupingTagKey) {
      case 'product_type':
        groupingTag = sampleReview.product_type || sampleReview.product_tags[0];
        break;

      case 'category':
        groupingTag = sampleReview.product_tags.find((t: string) =>
          t.toLowerCase().includes('category')
        ) || sampleReview.product_tags[0];
        break;

      case 'first':
      default:
        groupingTag = sampleReview.product_tags[0];
        break;
    }

    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .eq('admin_id', adminId)
      .contains('product_tags', [groupingTag])
      .eq('status', status)
      .order('created_at', { ascending: false })
      .limit(maxReviews);

    if (error) throw error;
    return data as Review[];
  },

  async updateReviewProductTags(
    reviewId: string,
    productTags: string[],
    productType?: string
  ): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .update({
        product_tags: productTags,
        product_type: productType,
        updated_at: new Date().toISOString()
      })
      .eq('id', reviewId);

    if (error) throw error;
  },

  async batchUpdateProductTags(
    updates: Array<{ reviewId: string; productTags: string[]; productType?: string }>
  ): Promise<void> {
    const promises = updates.map(update =>
      this.updateReviewProductTags(update.reviewId, update.productTags, update.productType)
    );

    await Promise.all(promises);
  },

  async getProductGroups(adminId: string) {
    const { data, error } = await supabase
      .from('product_groups')
      .select('*')
      .eq('admin_id', adminId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createProductGroup(
    adminId: string,
    groupName: string,
    productIds: string[]
  ) {
    const { data, error } = await supabase
      .from('product_groups')
      .insert({
        admin_id: adminId,
        group_name: groupName,
        product_ids: productIds,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateProductGroup(
    groupId: string,
    groupName: string,
    productIds: string[]
  ) {
    const { data, error } = await supabase
      .from('product_groups')
      .update({
        group_name: groupName,
        product_ids: productIds
      })
      .eq('id', groupId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteProductGroup(groupId: string) {
    const { error } = await supabase
      .from('product_groups')
      .delete()
      .eq('id', groupId);

    if (error) throw error;
  },

  async getReviewsForProductWithManualGrouping(
    adminId: string,
    productId: string,
    options?: { status?: ReviewStatus }
  ): Promise<Review[]> {
    const { data: groups } = await supabase
      .from('product_groups')
      .select('product_ids')
      .eq('admin_id', adminId)
      .contains('product_ids', [productId]);

    let productIdsToFetch = [productId];

    if (groups && groups.length > 0) {
      productIdsToFetch = groups[0].product_ids;
    }

    let query = supabase
      .from('reviews')
      .select('*')
      .eq('admin_id', adminId)
      .in('product_id', productIdsToFetch);

    if (options?.status) {
      query = query.eq('status', options.status);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data as Review[];
  },

  async fetchShopifyProducts(adminId: string) {
    console.log("🚀 Fetching Shopify Products for Admin ID:", adminId);
    
    if (!adminId) {
      throw new Error("Cannot fetch products: Admin ID is missing.");
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("No active session");

    const response = await fetch(`${FUNCTION_BASE_URL}/fetch-shopify-products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ admin_id: adminId })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Edge Function Error: ${response.statusText}`);
    }

    const data = await response.json();
    return data.products || [];
  },

  // ==========================================
  // ORDER NOTIFICATION SETTINGS
  // ==========================================

  async getOrderNotificationSettings(adminId: string) {
    const { data, error } = await supabase
      .from('notification_settings')
      .select('*')
      .eq('admin_id', adminId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching notification settings:', error);
      return null;
    }

    return data;
  },

  async updateOrderNotificationSettings(adminId: string, settings: any) {
    const { error } = await supabase
      .from('notification_settings')
      .upsert({
        admin_id: adminId,
        ...settings,
        updated_at: new Date().toISOString()
      }, { onConflict: 'admin_id' });

    if (error) throw error;
    return true;
  },
};
